import { Check, Info, Warning } from '@material-ui/icons';
import { Block, BlockProps } from 'baseui/block';
import { Notification } from 'baseui/notification';
import { ToastOverrides, ToastProps } from 'baseui/toast';
import { Label1 } from 'baseui/typography';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { $StyleProp } from 'styletron-react';
import { ThemeColours } from '../../styles/ThemeColours';

interface Props extends ToastProps {
  title?: string;
  hasIcon?: boolean;
  titleStyle?: $StyleProp<BlockProps>;
}

/** Extends Basweb's Notification component with an optional title, an icon, and a full-width display.
 *
 * When both `title` and `hasIcon` are supplied, the icon is displayed more prominently.
 */
export const HeNotification = forwardRef<HTMLElement, Props>(
  ({ title, children, hasIcon, kind, titleStyle, ...baseProps }: Props, ref) => {
    const hasTitle = Boolean(title);
    const Icon = Icons[kind ?? 'info'];
    const IconDisplay = hasTitle ? ChunkyIconBox : SubtleIconBox;

    return (
      <Notification overrides={ROOT_OVERRIDES} kind={kind} {...baseProps}>
        <Block display="flex" ref={ref}>
          {hasIcon && (
            <IconDisplay>
              <Icon />
            </IconDisplay>
          )}
          <Block display="flex" flexDirection="column" justifyContent="center">
            {hasTitle && (
              <Label1 color="currentColor" $style={titleStyle}>
                {title}
              </Label1>
            )}
            <Block>{children}</Block>
          </Block>
        </Block>
      </Notification>
    );
  },
);

const SubtleIconBox = styled.div`
  align-items: center;
  display: flex;
  margin-right: 16px;
`;

const ChunkyIconBox = styled(SubtleIconBox)`
  align-items: center;
  background-color: currentColor;
  border-radius: 100px;
  display: flex;
  flex-shrink: 0;
  height: 3em;
  justify-content: center;
  width: 3em;

  & path {
    fill: ${ThemeColours.white};
  }
`;

const Icons = {
  primary: Info,
  info: Info,
  negative: Warning,
  positive: Check,
  warning: Warning,
};

const BORDER_RADIUS = '4px';

const ROOT_OVERRIDES: ToastOverrides = {
  Body: {
    style: {
      opacity: 1,
      width: 'auto',
      borderTopRightRadius: BORDER_RADIUS,
      borderTopLeftRadius: BORDER_RADIUS,
      borderBottomRightRadius: BORDER_RADIUS,
      borderBottomLeftRadius: BORDER_RADIUS,
      fontWeight: 'normal',
    },
  },
};
